import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faDistributeSpacingHorizontal,
  faDistributeSpacingVertical,
} from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import {
  GetTeeSheetPrint,
  ITeeSheet,
  PostEmailTeeSheet,
  PostTeeSheetAuditTrails,
  PutTeeSheet,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { PostFacilityStatus, TPostFacilityStatus } from "api/rpc/2024-04/facilityAdmin/facility/status";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { formatDate, isEmpty } from "helpers/Helpers";

import { formatMarkdownBreaks } from "components/markdown/Markdown";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { Select } from "components/select/index";
import StatisticsBarBookingSummary, {
  EMPTY_BOOKING_SOURCES,
} from "elements/teesheet/statisticsbar/StatisticsBarBookingSummary";
import StatisticsBarExpandableSearch from "elements/teesheet/statisticsbar/StatisticsBarExpandableSearch";
import StatisticsBarMobileToolbar from "elements/teesheet/statisticsbar/StatisticsBarMobileToolbar";
import StatisticsBarFiltersSkeleton from "elements/teesheet/statisticsbar/StatisticsBarFiltersSkeleton";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import EmailTeesheetModal, { IEmailTeeSheet, TRecipient } from "elements/teesheet/statisticsbar/EmailTeesheetModal";
import PrintTeesheetModal, { IPrintTeesheet } from "elements/teesheet/statisticsbar/PrintTeesheetModal";
import StatusModal, { IFacilityStatusState } from "elements/teesheet/statisticsbar/StatusModal";

import { ICourse, IDivision } from "redux/reducers/models/facility";
import { ITeeTimeNew } from "redux/reducers/models/teetime";

import "elements/teesheet/statisticsbar/statisticsBar.scss";
import OverrideCartsAvailableModal, {
  IOverrideCartsAvailableModalState,
} from "elements/teesheet/statisticsbar/OverrideCartsAvailableModal";
import {
  ITeeSheetSideBySideSettings,
  IUpdateTeeSheetSideBySideSettings,
} from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";
import TeeSheetAuditTrailModal from "components/teeSheetAuditTrailModal/teeSheetAuditTrailModal";
import Portal from "elements/Portal";
import PrintCartSignsModal from "../modals/PrintCartSignsModal";
import { IPrintCartSignsModal } from "../NewTeeSheetTabs";
import Drawer from "components/drawerSlide/drawer";
import Checkbox from "components/form/checkbox/Checkbox";
import { cloneDeep } from "lodash";
import Toggle from "components/form/toggle/Toggle";

interface INewStatisticsBarProps {
  teeSheet: ITeeSheet;
  teeTimes: ITeeTimeNew[];
  reloadTeeTimesAndSheet: () => void;
  sideBySideSettings: ITeeSheetSideBySideSettings;
  updateSideBySideSettings: IUpdateTeeSheetSideBySideSettings;
  sideBySideSettingsKeyPrefix: string;
  teeSheetLoaded?: boolean;
  setDebouncedTeeTimeSearch: (teeTimeSearch: string) => void;
  currentCourseId?: number;
  currentDivisionId?: number;
  currentDivisions?: IDivision[];
  onCourseChange: (courseId: number) => void;
  onDivisionChange: (divisionId: number) => void;
  filtersDisabled?: boolean;
  combinedDivisionId: number;
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  windowWidth?: number;
}

//Redirecting from dashboard
interface ILocationState {
  params: { autoOpenDailyStatus: boolean };
}

export default function NewStatisticsBar(props: INewStatisticsBarProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  const dispatch = useAppDispatch();
  const { facilityStore, teeSheetStore, authStore } = useAppSelector(store => store);

  const {
    state: emailTeesheetModal,
    updateModal: updateEmailTeesheetModal,
    closeModal: closeEmailTeesheetModal,
  } = useModal<IEmailTeeSheet>({
    subject: "",
    content: "",
    recipientIds: [],
    newEmail: "",
    additionalEmails: [],
  });

  const {
    state: printTeesheetModal,
    updateModal: updatePrintTeesheetModal,
    closeModal: closePrintTeesheetModal,
  } = useModal<IPrintTeesheet>({
    startTime: "06:00",
    endTime: "21:00",
    date: null,
    fontSize: 8,
    selectedDivisionIds: [],
    selectedCourseIds: [],
    showCustomerType: false,
    showCartType: false,
    showGuest: false,
    showBagStorage: false,
    showCartNumber: false,
  });

  const {
    state: statusModal,
    updateModal: updateStatusModal,
    closeModal: closeStatusModal,
  } = useModal<IFacilityStatusState>({
    date: null,
    inputData: [] as Array<{ status_input_id: number; value: string }>,
  });

  const {
    state: overrideCartsAvailableModal,
    updateModal: updateOverrideCartsAvailableModal,
    closeModal: closeOverrideCartsAvailableModal,
  } = useModal<IOverrideCartsAvailableModalState>({
    cartsAvailableInput: "",
  });

  const {
    state: selectSideBySideSettingsModal,
    updateModal: updateSideBySideSettingsModal,
    closeModal: closeSideBySideSettingsModal,
  } = useModal();

  const [drawerSideBySideSettings, setDrawerSideBySideSettings] = useState<ITeeSheetSideBySideSettings>(null);

  const MINIMUM_ALLOWED_SELECTED_DIVISIONS = 1;
  const MAXIMUM_ALLOWED_SELECTED_DIVISIONS = 3;

  function openEditViewDrawer() {
    const settings = cloneDeep(props.sideBySideSettings);
    settings.enabled = settings.selectedDivisionIds.length >= 2;
    setDrawerSideBySideSettings(settings);
    updateSideBySideSettingsModal({ isOpen: true });
  }

  function closeEditViewDrawer() {
    setDrawerSideBySideSettings(null);
    closeSideBySideSettingsModal();
  }

  function setDrawerSelectedDivisionId(selectedDivisionId: number) {
    if (drawerSideBySideSettings.selectedDivisionIds.includes(selectedDivisionId)) {
      setDrawerSideBySideSettings(prev => {
        const updatedDivisionIds = prev.selectedDivisionIds.filter(id => id !== selectedDivisionId);
        return {
          ...prev,
          selectedDivisionIds: updatedDivisionIds,
          enabled: updatedDivisionIds.length >= 2,
        };
      });
    } else if (drawerSideBySideSettings.selectedDivisionIds.length < MAXIMUM_ALLOWED_SELECTED_DIVISIONS) {
      setDrawerSideBySideSettings(prev => {
        const updatedDivisionIds = [...prev.selectedDivisionIds, selectedDivisionId];
        return {
          ...prev,
          selectedDivisionIds: updatedDivisionIds,
          enabled: updatedDivisionIds.length >= 2,
        };
      });
    }
  }

  function clearDrawerSideBySideSettings() {
    setDrawerSideBySideSettings(prev => ({
      ...prev,
      enabled: prev.selectedDivisionIds.length >= 2,
      view: "stacked",
      hideTurnTimes: false,
      hideBlockedTimes: false,
      syncDivisionScroll: false,
      disableAutoScroll: false,
      hideBookingCategories: false,
    }));
  }

  const {
    state: auditTeeSheetModal,
    updateModal: updateAuditTeeSheetModal,
    closeModal: closeAuditTeeSheetModal,
  } = useModal({
    isOpen: false,
    date: null,
    teeSheetId: props.teeSheet?.id,
  });

  const {
    state: printCartSignsModal,
    updateModal: updatePrintCartSignsModal,
    closeModal: closePrintCartSignsModal,
  } = useModal<IPrintCartSignsModal>({
    league_id: undefined,
    tournament_id: undefined,
    selected_option: null,
    date: null,
    teetime: null,
    teetime_id: undefined,
    shotgun_application_id: null,
  });

  useEffect(() => {
    void autoOpenDailyStatus();
  }, []);

  function autoOpenDailyStatus() {
    const locationState = location.state as ILocationState;
    if (locationState?.params && locationState?.params?.autoOpenDailyStatus) {
      void updateStatusModal({ isOpen: true, date: teeSheetStore.selectedDate as Date });
    }
  }

  const cartFleetStatisticActive = props.teeSheet && facilityStore.teeSheetSettings?.validate_power_cart_count;

  const cartFleetSize = useMemo(() => {
    if (cartFleetStatisticActive) {
      if (props.teeSheet.carts_available != null) {
        return props.teeSheet.carts_available;
      }

      let updatedCartFleetSize = 0;

      if (facilityStore.teeSheetSettings?.use_cart_inventory) {
        updatedCartFleetSize = props.teeSheet.inventory_carts_available ?? props.teeSheet.default_carts_available ?? 0;
      } else {
        updatedCartFleetSize = props.teeSheet.default_carts_available ?? 0;
      }

      return updatedCartFleetSize;
    } else {
      return 0;
    }
  }, [
    props.teeSheet?.carts_available,
    props.teeSheet?.default_carts_available,
    props.teeSheet?.inventory_carts_available,
    facilityStore.teeSheetSettings?.use_cart_inventory,
  ]);

  const recipients: TRecipient[] = useMemo(() => {
    const updatedRecipients: TRecipient[] = [];

    if (!Array.isArray(props.teeTimes)) {
      return updatedRecipients;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type !== "crossover" && teeTime.slots[0].quantity_remaining !== 4) {
        updatedRecipients.push({
          id: teeTime.slots[0].tee_time_id,
          start_time: teeTime.start_time,
          division_id: teeTime.slots[0].division_id,
          course_id: teeTime.slots[0].course_id,
        });
      }
    });

    return updatedRecipients;
  }, [props.teeTimes]);

  const bookedSlotsCount = useMemo(() => {
    let count = 0;

    if (!Array.isArray(props.teeTimes)) {
      return count;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status === "booked") {
            count += 1;
          }
        });
      }
    });

    return count;
  }, [props.teeTimes]);

  const availableSlotsCount = useMemo(() => {
    let count = 0;

    if (!Array.isArray(props.teeTimes)) {
      return count;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status === "available") {
            count += 1;
          }
        });
      }
    });

    return count;
  }, [props.teeTimes]);

  const currentNoShows = useMemo(() => {
    const count: string[] = [];

    if (!Array.isArray(props.teeTimes)) {
      return {
        total: 0,
        no_show_charge: 0,
        no_show_weather: 0,
        no_show_defer: 0,
      };
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.customer_id && slot.check_in_status === "no_show") {
            count.push(slot.no_show_type);
          }
        });
      }
    });

    // Add more filters to return obj for more no_shows
    return {
      total: count.length,
      no_show_charge: count.filter(val => val === "charge").length,
      no_show_weather: count.filter(val => val === "weather").length,
      no_show_defer: count.filter(val => val === "defer").length,
    };
  }, [props.teeTimes]);

  const currentBookingSources = useMemo(() => {
    const counts = { ...EMPTY_BOOKING_SOURCES };

    if (!Array.isArray(props.teeTimes)) {
      return counts;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status !== "booked") {
            return;
          }

          const source = slot.tee_time_bookings_source;
          if (source && source in counts) {
            counts[source as keyof typeof counts] += 1;
          } else if (source) {
            counts.other += 1;
          } else {
            counts.tee_sheet += 1;
          }
        });
      }
    });

    return counts;
  }, [props.teeTimes]);

  async function sendTeesheetEmail() {
    const formattedDate = formatDate(teeSheetStore.selectedDate as Date);

    const params = {
      date: formattedDate,
      subject: emailTeesheetModal.subject,
      content: formatMarkdownBreaks(emailTeesheetModal.content),
      tee_time_ids: emailTeesheetModal.recipientIds,
      additional_email_addresses: emailTeesheetModal.additionalEmails,
    };

    const res = await PostEmailTeeSheet(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error sending tee sheet email"));
      return;
    }

    closeEmailTeesheetModal();
  }

  async function printTeesheet() {
    const formattedDate = formatDate(printTeesheetModal.date);

    const params = {
      date: formattedDate,
      start_time: printTeesheetModal.startTime,
      end_time: printTeesheetModal.endTime,
      division_ids: printTeesheetModal.selectedDivisionIds,
      course_ids: printTeesheetModal.selectedCourseIds,
      font_size: printTeesheetModal.fontSize,
      show_bag_storage: printTeesheetModal.showBagStorage,
      show_customer_type: printTeesheetModal.showCustomerType,
      show_cart_type: printTeesheetModal.showCartType,
      show_cart_number: printTeesheetModal.showCartNumber,
    };

    const teeSheetRes = await GetTeeSheetPrint(params, true);

    if (teeSheetRes.status === StatusCode.OK) {
      window.open().document.write(String(teeSheetRes.data));
    } else {
      dispatch(showError(t("Error printing teesheet")));
    }
  }

  async function saveFacilityStatus() {
    const { ...rest } = statusModal;
    const params: TPostFacilityStatus = {
      date: formatDate(rest.date),
      input_data: rest.inputData,
    };

    const res = await PostFacilityStatus(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving facility status.")));
      return;
    }

    dispatch(showSuccess(t("Successfully saved the facility status.")));
    closeStatusModal();
  }

  async function saveOverrideCartsAvailable() {
    if (!cartFleetStatisticActive) {
      return;
    }

    const updatedCartsAvailable = isEmpty(overrideCartsAvailableModal.cartsAvailableInput)
      ? null
      : Number(overrideCartsAvailableModal.cartsAvailableInput);

    const putTeeSheetRes = await PutTeeSheet(
      {
        id: props.teeSheet?.id,
        carts_available: updatedCartsAvailable,
      },
      true,
    );

    if (putTeeSheetRes.status !== StatusCode.OK) {
      dispatch(showSuccess("Cart count successfully overidden"));
      return;
    }

    const auditParams = {
      tee_sheet_id: props.teeSheet?.id,
      user_id: authStore.user.id,
      action: "modified",
      title: "Tee Sheet Carts Update",
      description: `Tee Sheet Carts overridden from ${cartFleetSize} to ${updatedCartsAvailable}`,
    };

    const postAudit = await PostTeeSheetAuditTrails(auditParams, false);

    if (postAudit.status !== StatusCode.OK) {
      return;
    }

    props.reloadTeeTimesAndSheet();

    updateOverrideCartsAvailableModal({ isOpen: false });
  }

  function openOverrideCartsAvailableModal() {
    if (cartFleetStatisticActive) {
      updateOverrideCartsAvailableModal({ isOpen: true });
    }
  }

  function saveSideBySideSettings(updatedSideBySideSettings: ITeeSheetSideBySideSettings) {
    props.updateSideBySideSettings(props.sideBySideSettingsKeyPrefix, updatedSideBySideSettings);
    closeEditViewDrawer();
  }

  const isMobile = (props.windowWidth ?? 0) <= MOBILE_WIDTH && props.onDateChange != null;

  const toolbarDateFallbackRef = useRef(new Date());
  const toolbarDate = props.selectedDate ?? toolbarDateFallbackRef.current;

  const filtersReady =
    props.currentCourseId != null && props.currentDivisionId != null && props.currentDivisions != null;

  const filterSkeletonCount = useMemo(() => {
    let count = 1;
    const courseCount = facilityStore?.facility?.courses?.length;
    if (courseCount == null || courseCount > 1) {
      count++;
    }
    return count;
  }, [facilityStore?.facility?.courses?.length]);

  const optionsMenu = (
    <NavigationDropdownNew
      rightAlign
      hideChevron
      type="secondary"
      label={<FontAwesomeIcon icon={faEllipsisVertical} />}
      sections={[
        [
          {
            label: t("Print"),
            onClick: () => updatePrintTeesheetModal({ isOpen: true, date: teeSheetStore.selectedDate as Date }),
            icon: "print",
          },
        ],
        [
          {
            label: t("Print Cart Signs"),
            onClick: () => {
              const teeTimes = Array.isArray(props.teeTimes) ? props.teeTimes : [];
              const validTeeTime = teeTimes.find(t => !t.slots[0]?.tournament_id && !t.slots[0]?.league_id);

              updatePrintCartSignsModal({
                isOpen: true,
                teetime: validTeeTime ?? teeTimes[0],
              });
            },
            icon: "print",
            hidden: false,
          },
        ],
        [
          {
            label: t("Daily Status"),
            onClick: () => updateStatusModal({ isOpen: true, date: teeSheetStore.selectedDate as Date }),
            icon: "calendars",
            hidden: !authStore?.user?.permissions?.edit_daily_status,
          },
        ],
        [
          {
            label: t("Email"),
            onClick: () => updateEmailTeesheetModal({ isOpen: true }),
            icon: "envelope",
            hidden: false,
          },
        ],
        [
          {
            label: t("Edit Tee Sheet"),
            onClick: () => history.push("/admin/teesheet/edit"),
            icon: "edit",
            hidden: false,
          },
        ],
        [
          {
            label: t("Edit View"),
            onClick: () => openEditViewDrawer(),
            icon: "table-columns",
            disabled: props.sideBySideSettingsKeyPrefix == null,
            hidden: false,
          },
        ],
        [
          {
            label: t("Audit Tee Sheet"),
            onClick: () => updateAuditTeeSheetModal({ isOpen: true, date: teeSheetStore.selectedDate }),
            icon: "history",
            hidden: false,
          },
        ],
      ]}
    />
  );

  return (
    <div className={classNames("statistics-bar statistics-bar--teesheet", { "statistics-bar--mobile": isMobile })}>
      {isMobile && (
        <StatisticsBarMobileToolbar
          selectedDate={toolbarDate}
          onDateChange={props.onDateChange}
          search={
            <StatisticsBarExpandableSearch
              placeholder={t("Search")}
              setDebouncedTeeTimeSearch={props.setDebouncedTeeTimeSearch}
              mobileOverlay
            />
          }
          menu={optionsMenu}
        />
      )}
      <div className="statistics-bar-booking-content">
        <StatisticsBarBookingSummary
          isLoading={!props.teeTimes}
          bookedCount={bookedSlotsCount}
          availableCount={availableSlotsCount}
          noShows={currentNoShows}
          bookingSources={currentBookingSources}
          cartCount={cartFleetSize}
          onCartClick={cartFleetStatisticActive ? openOverrideCartsAvailableModal : undefined}
          compact={isMobile}
        />
      </div>
      <div className="statistics-bar-action-content">
        <div className="statistics-bar-filters">
          {!isMobile && (
            <StatisticsBarExpandableSearch
              placeholder={t("Search")}
              setDebouncedTeeTimeSearch={props.setDebouncedTeeTimeSearch}
            />
          )}
          {!filtersReady ? (
            <StatisticsBarFiltersSkeleton count={filterSkeletonCount} />
          ) : (
            <>
              {facilityStore?.facility?.courses?.length > 1 && (
                <div className="statistics-bar-filter">
                  <Select
                    placeholder={t("Course")}
                    onChange={(val: number) => props.onCourseChange(val)}
                    defaultValue={props.currentCourseId}
                    disabled={props.filtersDisabled}
                  >
                    {facilityStore.facility.courses.map((course: ICourse, i: number) => (
                      <Select.Option key={i} value={course.id} name={course.long_name}>
                        <span>{course.long_name}</span>
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
              <div className="statistics-bar-filter">
                <Select
                  placeholder={t("Division")}
                  onChange={(val: number) => props.onDivisionChange(val)}
                  defaultValue={props.currentDivisionId}
                  disabled={props.filtersDisabled}
                >
                  {props.currentDivisions.map((division: IDivision, i: number) => (
                    <Select.Option key={i} value={division.id} name={division.title}>
                      <span>{division.title}</span>
                    </Select.Option>
                  ))}
                  <Select.Option value={props.combinedDivisionId} name="Combined">
                    <span>{t("Combined")}</span>
                  </Select.Option>
                </Select>
              </div>
            </>
          )}
        </div>

        {!isMobile && <div className="statistics-bar-action-content-actions">{optionsMenu}</div>}
      </div>

      <EmailTeesheetModal
        state={emailTeesheetModal}
        update={updateEmailTeesheetModal}
        close={closeEmailTeesheetModal}
        recipients={recipients}
        onOk={() => void sendTeesheetEmail()}
        courses={facilityStore.facility?.courses}
      />

      <PrintTeesheetModal
        state={printTeesheetModal}
        update={updatePrintTeesheetModal}
        close={closePrintTeesheetModal}
        onOk={() => void printTeesheet()}
        facility={facilityStore.facility}
      />

      <StatusModal
        state={statusModal}
        update={updateStatusModal}
        close={closeStatusModal}
        onOk={() => void saveFacilityStatus()}
      />

      <OverrideCartsAvailableModal
        cartsAvailable={props.teeSheet?.carts_available}
        state={overrideCartsAvailableModal}
        update={updateOverrideCartsAvailableModal}
        close={closeOverrideCartsAvailableModal}
        onOk={() => void saveOverrideCartsAvailable()}
      />

      <Drawer
        open={selectSideBySideSettingsModal.isOpen}
        title={t("Tee sheet view")}
        subtitle={`${drawerSideBySideSettings?.view === "side-by-side" ? t("Side by side") : t("Stacked")} • ${
          [
            drawerSideBySideSettings?.syncDivisionScroll,
            drawerSideBySideSettings?.enabled,
            drawerSideBySideSettings?.hideTurnTimes,
            drawerSideBySideSettings?.hideBlockedTimes,
          ].filter(Boolean).length
        } ${t("filters active")}`}
        onClose={closeEditViewDrawer}
        onOk={() => void saveSideBySideSettings(drawerSideBySideSettings)}
        okText={t("Confirm")}
        okDisabled={
          drawerSideBySideSettings == null ||
          drawerSideBySideSettings.selectedDivisionIds.length < MINIMUM_ALLOWED_SELECTED_DIVISIONS ||
          drawerSideBySideSettings.selectedDivisionIds.length > MAXIMUM_ALLOWED_SELECTED_DIVISIONS
        }
        showCancel
        cancelText={t("Clear")}
        onCancel={clearDrawerSideBySideSettings}
        backDropClose
      >
        {drawerSideBySideSettings && (
          <>
            {/* Divisions */}
            <Drawer.Section bordered>
              <p className="edit-view-drawer-section-title">{t("Divisions")}</p>
              <div className="edit-view-drawer-divisions">
                {facilityStore.facility?.courses?.map(course => (
                  <React.Fragment key={course.id}>
                    {course?.divisions?.map(division => {
                      const selectedDivision = drawerSideBySideSettings.selectedDivisionIds.some(
                        id => id === division.id,
                      );
                      return (
                        <Checkbox
                          key={division.id}
                          size="small"
                          label={division.title}
                          value={division.id}
                          checked={selectedDivision}
                          disabled={
                            !selectedDivision &&
                            drawerSideBySideSettings.selectedDivisionIds.length >= MAXIMUM_ALLOWED_SELECTED_DIVISIONS
                          }
                          onChange={() => setDrawerSelectedDivisionId(division.id)}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </Drawer.Section>

            {/* Layout */}
            {drawerSideBySideSettings.selectedDivisionIds.length >= 2 && (
              <Drawer.Section bordered>
                <p className="edit-view-drawer-section-title">{t("Layout")}</p>
                <p className="edit-view-drawer-section-label">{t("Division layout")}</p>
                <div className="edit-view-drawer-views">
                  <button
                    type="button"
                    className={classNames("edit-view-drawer-view", {
                      "edit-view-drawer-view-active": drawerSideBySideSettings.view === "stacked",
                    })}
                    onClick={() => setDrawerSideBySideSettings(prev => ({ ...prev, view: "stacked" }))}
                  >
                    <FontAwesomeIcon icon={faDistributeSpacingVertical} />
                    <span>{t("Stacked")}</span>
                  </button>
                  <button
                    type="button"
                    className={classNames("edit-view-drawer-view", {
                      "edit-view-drawer-view-active": drawerSideBySideSettings.view === "side-by-side",
                    })}
                    onClick={() => setDrawerSideBySideSettings(prev => ({ ...prev, view: "side-by-side" }))}
                  >
                    <FontAwesomeIcon icon={faDistributeSpacingHorizontal} />
                    <span>{t("Side by side")}</span>
                  </button>
                </div>
                <div className="edit-view-drawer-toggles">
                  <Toggle
                    labelRight={t("Sync scrolling")}
                    labelSupport={t("Keep division columns aligned when scrolling.")}
                    size="small"
                    checked={drawerSideBySideSettings.syncDivisionScroll}
                    onChange={() =>
                      setDrawerSideBySideSettings(prev => ({ ...prev, syncDivisionScroll: !prev.syncDivisionScroll }))
                    }
                  />
                </div>
              </Drawer.Section>
            )}

            {/* Display options */}
            <Drawer.Section>
              <p className="edit-view-drawer-section-title">{t("Display options")}</p>
              <div className="edit-view-drawer-toggles">
                <Toggle
                  labelRight={t("Hide turn times")}
                  labelSupport={t("Collapse the 9-hole turn row from the tee sheet.")}
                  size="small"
                  checked={drawerSideBySideSettings.hideTurnTimes}
                  onChange={() =>
                    setDrawerSideBySideSettings(prev => ({ ...prev, hideTurnTimes: !prev.hideTurnTimes }))
                  }
                />
                <Toggle
                  labelRight={t("Hide blocked times")}
                  labelSupport={t("Remove blocked tee times from view.")}
                  size="small"
                  checked={drawerSideBySideSettings.hideBlockedTimes}
                  onChange={() =>
                    setDrawerSideBySideSettings(prev => ({ ...prev, hideBlockedTimes: !prev.hideBlockedTimes }))
                  }
                />
                <Toggle
                  labelRight={t("Disable auto scroll")}
                  size="small"
                  labelSupport={t("Disable the auto scroll to the current time.")}
                  checked={drawerSideBySideSettings.disableAutoScroll}
                  onChange={() =>
                    setDrawerSideBySideSettings(prev => ({ ...prev, disableAutoScroll: !prev.disableAutoScroll }))
                  }
                />
                <Toggle
                  labelRight={t("Hide booking categories")}
                  size="small"
                  labelSupport={t("Hide the booking category icons from the tee sheet.")}
                  checked={drawerSideBySideSettings.hideBookingCategories}
                  onChange={() =>
                    setDrawerSideBySideSettings(prev => ({
                      ...prev,
                      hideBookingCategories: !prev.hideBookingCategories,
                    }))
                  }
                />
              </div>
            </Drawer.Section>
          </>
        )}
      </Drawer>

      <TeeSheetAuditTrailModal
        state={auditTeeSheetModal}
        update={updateAuditTeeSheetModal}
        close={closeAuditTeeSheetModal}
        onOk={() => void closeAuditTeeSheetModal()}
      />

      {/* PRINT CART SIGNS MODAL */}
      <Portal isMounted={printCartSignsModal.isOpen}>
        <PrintCartSignsModal
          open={printCartSignsModal.isOpen}
          teetime={printCartSignsModal.teetime}
          teeTimes={props.teeTimes}
          tournaments={[]}
          leagues={[]}
          onOk={closePrintCartSignsModal}
          onCancel={closePrintCartSignsModal}
        />
      </Portal>
    </div>
  );
}
